import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogDentalImplants = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="/images/18.png"
          alt="Dental Implants"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-blue-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">DENTAL</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              Dental Implants: Everything You Need to Know Before Getting One
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />28 Mar 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />8 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">Dental Implants: Complete Patient Guide</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Article */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-[#39a5d3] pl-5 bg-blue-50 py-4 pr-4 rounded-r-lg">
                Dental implants are now considered the gold standard treatment for replacing missing teeth — and for good reason. Unlike dentures that sit on the gums or bridges that require grinding down adjacent healthy teeth, implants replace the tooth root itself, providing a stable, permanent foundation for a crown that looks, feels, and functions like a natural tooth. If you are considering an implant, this guide will walk you through everything you need to know before committing to the procedure.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">What Is a Dental Implant?</h2>
              <p className="mb-4">A dental implant is a small titanium screw — typically 3 to 5 millimetres in diameter and 8 to 16 millimetres long — that is surgically placed into the jawbone to replace a missing tooth root. Over the following 3 to 6 months, the implant fuses with the surrounding bone through a process called osseointegration, becoming permanently anchored.</p>
              <p className="mb-8">Once the implant has integrated, a connector piece called an abutment is attached, and a custom-made ceramic crown is placed on top. The finished result is a tooth replacement that is indistinguishable from natural teeth in appearance and that restores full biting force. A well-placed, well-maintained implant can last 20 to 30 years or more — often a lifetime.</p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">Are You a Good Candidate?</h2>
              <p className="mb-5">Not everyone is immediately suitable for dental implants. Your dentist will assess the following factors:</p>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Sufficient Bone Volume', d: 'The implant needs adequate bone to anchor into. Patients who have had a missing tooth for several years often experience bone resorption — the jawbone gradually shrinks when it no longer has a tooth root to support. In these cases, a bone graft procedure is performed before or during implant placement to rebuild the necessary volume. This extends treatment time but produces excellent results.' },
                  { t: 'Healthy Gums', d: 'Active gum disease (periodontitis) must be treated before implant placement. Untreated gum disease significantly increases the risk of implant failure through peri-implantitis — infection around the implant. Your dentist will assess your gum health and complete any necessary periodontal treatment before proceeding.' },
                  { t: 'General Health', d: 'Uncontrolled diabetes, bleeding disorders, and certain medications — particularly bisphosphonates used for osteoporosis — can affect healing and implant integration. Smokers have a significantly higher implant failure rate due to impaired blood supply and immune function. Heavy smokers are generally advised to quit before implant treatment.' },
                  { t: 'Age', d: 'Implants are not placed in patients whose jaws are still growing — typically under 18 years old. There is no upper age limit; implants are successfully placed in patients well into their 80s, provided general health allows the procedure.' },
                  { t: 'Commitment to Oral Hygiene', d: 'Implants do not decay, but the gum and bone around them can be affected by poor oral hygiene just as natural teeth can. Long-term implant success requires thorough daily cleaning with a soft brush and interdental brushes, and regular professional maintenance appointments.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src="/images/19.png"
                    alt="Dental treatment"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">Modern implant dentistry achieves results that are virtually indistinguishable from natural teeth.</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">The Implant Procedure Step by Step</h3>
                  <p className="text-sm leading-relaxed mb-3">The implant process takes place over several months, though you are not in the chair for most of that time — the majority is healing time.</p>
                  <p className="text-sm leading-relaxed mb-3">The surgical placement itself typically takes 30 to 90 minutes under local anaesthetic and is generally well-tolerated. Most patients are surprised by how comfortable the procedure is.</p>
                  <p className="text-sm leading-relaxed">In some cases, a temporary crown can be placed immediately after implant surgery — this is called immediate loading and suits specific clinical situations. In most cases, a healing period of 3 to 6 months is allowed before the final crown is fitted.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">The Complete Treatment Timeline</h2>
              <div className="space-y-4 mb-10">
                {[
                  { n: '01', t: 'Initial Consultation and Planning', d: 'Your dentist takes X-rays and often a 3D CBCT scan to assess bone volume, identify nerve positions, and plan implant placement precisely. Treatment options, costs, and timelines are discussed in detail. Any necessary preparatory treatment — extractions, gum treatment, or bone grafting — is planned at this stage.' },
                  { n: '02', t: 'Bone Grafting (if required)', d: 'If bone volume is insufficient, a graft is placed — typically using synthetic bone material, bone from another site, or a combination. Healing takes 3 to 6 months before implant placement. Not every patient needs this step.' },
                  { n: '03', t: 'Implant Placement Surgery', d: 'A small incision is made in the gum, a precisely sized channel is drilled into the bone, and the titanium implant is placed. The gum is closed with dissolvable stitches. Most patients return to work the following day, though some swelling and tenderness persists for a few days.' },
                  { n: '04', t: 'Osseointegration — Healing Period', d: 'The implant fuses with the surrounding bone over 3 to 6 months. During this time you will typically wear a temporary restoration. Regular check-up appointments allow your dentist to monitor integration progress.' },
                  { n: '05', t: 'Abutment and Crown Placement', d: 'Once integration is confirmed, the abutment is attached and impressions are taken for the custom ceramic crown. The final crown is fitted 2 to 3 weeks later. Your dentist checks the bite and makes any necessary adjustments — the result should be completely comfortable and natural-feeling.' },
                ].map(item => (
                  <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                    <div className="bg-[#19354a] text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">Cost of Dental Implants in India</h2>
              <p className="mb-5">Implant costs vary considerably based on the brand of implant, the clinic's location, and what additional procedures are required. Here is a realistic breakdown:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  { label: 'Single implant (implant + abutment + crown)', cost: '₹25,000 – ₹80,000' },
                  { label: 'Bone graft (if required)', cost: '₹15,000 – ₹40,000' },
                  { label: 'CBCT scan', cost: '₹2,000 – ₹5,000' },
                  { label: 'Full mouth implants', cost: '₹3,00,000 – ₹10,00,000+' },
                ].map((item, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className="font-bold text-[#19354a] text-sm">{item.cost}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Questions to Ask Your Dentist Before Proceeding</h2>
                <ul className="space-y-2">
                  {[
                    'Which implant brand do you use and what is the failure rate for this brand?',
                    'Do you have a CBCT scanner or will I need to go elsewhere for 3D imaging?',
                    'Will I need a bone graft and if so, what is the source of the bone material?',
                    'What is included in the quoted price — consultation, scans, surgery, crown, and follow-ups?',
                    'What happens if the implant fails — do you replace it at no cost?',
                    'How many implants have you placed and can I see before-and-after photos of similar cases?',
                    'What anaesthetic will be used and what are my options?',
                    'How should I care for the implant and crown long-term?',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39a5d3] mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Single implant cost in India', v: '₹25K–₹80K' },
                    { l: 'Implant lifespan', v: '20–30+ years' },
                    { l: 'Osseointegration time', v: '3–6 months' },
                    { l: 'Success rate', v: '95–98%' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Implants vs Alternatives</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { label: 'Longevity', implant: 'Lifetime', bridge: '10–15 yrs', denture: '5–8 yrs' },
                    { label: 'Adjacent teeth', implant: 'Untouched', bridge: 'Ground down', denture: 'Unaffected' },
                    { label: 'Bone preservation', implant: 'Yes', bridge: 'No', denture: 'No' },
                    { label: 'Feel', implant: 'Natural', bridge: 'Good', denture: 'Artificial' },
                  ].map((row, i) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs font-bold text-gray-700 mb-1.5">{row.label}</p>
                      <div className="flex justify-between text-xs">
                        <span className="text-green-600 font-medium">Implant: {row.implant}</span>
                        <span className="text-gray-400">Bridge: {row.bridge}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Book a Dental Consultation</h3>
                <p className="text-white/80 text-xs mb-4">Find verified implant dentists on Docmize.</p>
                <Link to="/doctors?specialty=Dentist" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Dentist
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDentalImplants;